'use client';

import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LocaleLink } from '@/components/locale-link';

// Source names and URLs are proper nouns / language-neutral and deliberately
// not translated; the per-row gloss and data types come from i18n.
type SourceRow = {
  key: string; // i18n key under pages.sources.rows.*
  href: string;
  domain: string;
};

type CountryGroup = {
  key: 'germany' | 'switzerland';
  flag: string;
  rows: SourceRow[];
};

const COUNTRY_GROUPS: CountryGroup[] = [
  {
    key: 'germany',
    flag: '/media/flags/germany.svg',
    rows: [
      {
        key: 'unternehmensregister',
        href: 'https://www.unternehmensregister.de',
        domain: 'unternehmensregister.de',
      },
      {
        key: 'handelsregister',
        href: 'https://www.handelsregister.de',
        domain: 'handelsregister.de',
      },
      {
        key: 'insolvenzbekanntmachungen',
        href: 'https://neu.insolvenzbekanntmachungen.de',
        domain: 'neu.insolvenzbekanntmachungen.de',
      },
    ],
  },
  {
    key: 'switzerland',
    flag: '/media/flags/switzerland.svg',
    rows: [
      { key: 'zefix', href: 'https://www.zefix.ch', domain: 'zefix.ch' },
      {
        key: 'shab',
        href: 'https://amtsblattportal.ch',
        domain: 'amtsblattportal.ch',
      },
      {
        key: 'lindas',
        href: 'https://lindas.admin.ch',
        domain: 'lindas.admin.ch',
      },
    ],
  },
];

const CROSS_BORDER_ROWS: SourceRow[] = [
  { key: 'ted', href: 'https://ted.europa.eu', domain: 'ted.europa.eu' },
  { key: 'gleif', href: 'https://www.gleif.org', domain: 'gleif.org' },
];

export function SourcesContent() {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-5 lg:gap-7.5">
      <Card>
        <CardHeader>
          <CardTitle as="h1">{t('pages.sources.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-foreground">{t('pages.sources.intro')}</p>

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-3 py-2 font-medium">
                    {t('pages.sources.tableCountry')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('pages.sources.tableTypes')}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t('pages.sources.tableSource')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {COUNTRY_GROUPS.map((group) =>
                  group.rows.map((row, i) => (
                    <tr
                      key={row.key}
                      className="border-b border-border/50 last:border-0"
                    >
                      {i === 0 && (
                        <td
                          rowSpan={group.rows.length}
                          className="whitespace-nowrap border-r border-border/50 px-3 py-2 align-top"
                        >
                          <span className="flex items-center gap-2 font-medium text-foreground">
                            <img
                              src={group.flag}
                              alt=""
                              className="h-4 w-4 shrink-0 rounded-sm object-contain"
                            />
                            {t(`pages.sources.countries.${group.key}`)}
                          </span>
                        </td>
                      )}
                      <td className="px-3 py-2 text-muted-foreground">
                        {t(`pages.sources.rows.${row.key}.types`)}
                      </td>
                      <td className="px-3 py-2">
                        <a
                          href={row.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-primary hover:underline"
                        >
                          {t(`pages.sources.rows.${row.key}.name`)}
                        </a>
                        <div className="text-xs text-muted-foreground">
                          {row.domain}
                        </div>
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-foreground">
              {t('pages.sources.crossBorderTitle')}
            </h2>
            <ul className="list-disc space-y-1 ps-5 text-sm text-muted-foreground">
              {CROSS_BORDER_ROWS.map((row) => (
                <li key={row.key}>
                  <a
                    href={row.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    {t(`pages.sources.rows.${row.key}.name`)}
                  </a>{' '}
                  — {t(`pages.sources.rows.${row.key}.types`)}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-muted-foreground">
            {t('pages.sources.geoNote')} (
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              &copy; OpenStreetMap
            </a>
            ).
          </p>

          <p className="text-sm text-foreground">
            {t('pages.sources.qualityNote')}{' '}
            <LocaleLink
              href="/datenqualitaet"
              className="font-medium text-primary hover:underline"
            >
              {t('navigation.dataQuality')}
            </LocaleLink>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
